import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { ReferenzaFirestore } from '../../../core/models/firestore/referenza.model';
import { ApiService } from '../../../services/api.service';
import {
  GeocodingService,
  type GeocodeResult,
} from '../../../services/geocoding.service';
import type { LeafletLatLng } from '../leaflet-map/leaflet-map.component';

@Component({
  selector: 'app-posizione-referenza-dialog',
  templateUrl: './posizione-referenza-dialog.component.html',
  styleUrl: './posizione-referenza-dialog.component.scss',
})
export class PosizioneReferenzaDialogComponent {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() referenza: ReferenzaFirestore | null = null;

  /** Disabilita salvataggio se non permesso */
  @Input() canModify = true;

  @Output() posizioneSaved = new EventEmitter<void>();

  saving = false;
  searching = false;

  lat: number | null = null;
  lng: number | null = null;

  addressInput = '';
  cityInput = '';
  // teniamo i campi per debug interno / futuri miglioramenti (non mostrati in UI)
  lastGeocodeLabel: string | null = null;
  lastGeocodeConfidence: GeocodeResult['confidence'] | null = null;
  mapResizeToken = 0;

  readonly defaultCenter: LeafletLatLng = { lat: 41.9, lng: 12.5 };
  mapCenter: LeafletLatLng = this.defaultCenter;
  mapZoom = 5;
  selectedPosition: LeafletLatLng | null = null;
  private initialLat: number | null = null;
  private initialLng: number | null = null;
  isDirty = false;

  // Editor coordinate (inline, compatto)
  showCoordsEditor = false;
  coordEditLat: number | null = null;
  coordEditLng: number | null = null;

  constructor(
    private apiService: ApiService,
    private geocodingService: GeocodingService,
    private messageService: MessageService,
  ) {}

  onShow(): void {
    const pos = this.referenza?.posizione ?? null;
    this.lat = pos ? pos.latitude : null;
    this.lng = pos ? pos.longitude : null;
    this.lastGeocodeLabel = null;
    this.lastGeocodeConfidence = null;

    // Non sovrascriviamo i campi se l'utente li stava usando, ma per coerenza li resettiamo all'apertura
    this.addressInput = '';
    this.cityInput = '';

    this.initialLat = this.lat;
    this.initialLng = this.lng;
    this.isDirty = false;
    this.showCoordsEditor = false;
    this.coordEditLat = this.lat;
    this.coordEditLng = this.lng;

    this.syncSelectedFromLatLng();
    // Forza repaint Leaflet quando il dialog diventa visibile
    this.mapResizeToken += 1;
    // ulteriore tick dopo animazione PrimeNG
    setTimeout(() => (this.mapResizeToken += 1), 250);
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  removePosizione(): void {
    this.lat = null;
    this.lng = null;
    this.syncSelectedFromLatLng();
    this.updateDirty();
    this.showCoordsEditor = false;
  }

  onMapSelectedPositionChange(pos: LeafletLatLng | null): void {
    if (!pos) {
      this.lat = null;
      this.lng = null;
      this.syncSelectedFromLatLng();
      this.coordEditLat = null;
      this.coordEditLng = null;
      return;
    }
    this.lat = pos.lat;
    this.lng = pos.lng;
    this.syncSelectedFromLatLng();
    this.updateDirty();
    this.coordEditLat = this.lat;
    this.coordEditLng = this.lng;

    // Best-effort reverse (Nominatim): aggiorna la label, senza bloccare l'UI
    this.geocodingService.reverse(pos.lat, pos.lng).subscribe({
      next: (r) => {
        if (r?.label) {
          this.lastGeocodeLabel = r.label;
          this.lastGeocodeConfidence = 'exact';
        }
      },
      error: () => {
        // ignora errori di rete/rate-limit
      },
    });
  }

  normalize(): void {
    if (this.lat !== null && this.lat !== undefined) {
      this.lat = Math.max(-90, Math.min(90, this.lat));
    }
    if (this.lng !== null && this.lng !== undefined) {
      this.lng = Math.max(-180, Math.min(180, this.lng));
    }
  }

  openCoordsEditor(): void {
    if (!this.canModify) return;
    this.showCoordsEditor = true;
    this.coordEditLat = this.lat;
    this.coordEditLng = this.lng;
  }

  cancelCoordsEdit(): void {
    this.showCoordsEditor = false;
    this.coordEditLat = this.lat;
    this.coordEditLng = this.lng;
  }

  applyCoordsEdit(): void {
    if (!this.canModify) return;
    const lat = this.coordEditLat;
    const lng = this.coordEditLng;
    if (lat === null || lat === undefined || lng === null || lng === undefined) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Inserisci sia latitudine che longitudine.',
        life: 3500,
      });
      return;
    }

    const clampedLat = Math.max(-90, Math.min(90, lat));
    const clampedLng = Math.max(-180, Math.min(180, lng));
    this.coordEditLat = clampedLat;
    this.coordEditLng = clampedLng;

    this.onMapSelectedPositionChange({ lat: clampedLat, lng: clampedLng });
    this.showCoordsEditor = false;
  }

  async copyCoords(): Promise<void> {
    if (this.lat === null || this.lng === null) return;
    const text = `${this.lat.toFixed(6)}, ${this.lng.toFixed(6)}`;
    try {
      await navigator.clipboard.writeText(text);
      this.messageService.add({
        severity: 'success',
        summary: 'Copiato',
        detail: 'Coordinate copiate negli appunti',
        life: 2000,
      });
    } catch {
      // fallback: nessun toast di errore, evitiamo rumore
      this.messageService.add({
        severity: 'info',
        summary: 'Info',
        detail: 'Impossibile copiare automaticamente. Seleziona e copia manualmente.',
        life: 3500,
      });
    }
  }

  async searchByAddressAndCity(): Promise<void> {
    if (!this.canModify) return;

    const address = (this.addressInput || '').trim();
    const city = (this.cityInput || '').trim();

    if (!address && !city) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Inserisci almeno la città oppure un indirizzo.',
        life: 3500,
      });
      return;
    }

    this.searching = true;
    this.lastGeocodeLabel = null;
    this.lastGeocodeConfidence = null;

    try {
      // 1) Prova query completa (indirizzo + città)
      const fullQuery = [address, city].filter(Boolean).join(', ');
      let results = await firstValueFrom(
        this.geocodingService.search(fullQuery, 5),
      );

      // 2) Fallback: se non trova nulla o se l'utente ha inserito solo address senza city, prova city-only
      if ((!results || results.length === 0) && city) {
        results = await firstValueFrom(this.geocodingService.search(city, 5));
      }

      if (!results || results.length === 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Nessun risultato',
          detail:
            'Non sono state trovate coordinate. Prova a specificare meglio indirizzo e città.',
          life: 4500,
        });
        return;
      }

      // Heuristics: preferisci risultati "exact" se presenti
      const best = results.find((r) => r.confidence === 'exact') ?? results[0];

      this.lat = best.lat;
      this.lng = best.lng;
      this.normalize();
      this.syncSelectedFromLatLng();
      // Se è una ricerca approssimativa (es. solo città), evitiamo zoom troppo spinto
      this.mapZoom = best.confidence === 'exact' ? 16 : 13;
      this.updateDirty();

      this.lastGeocodeLabel = best.label;
      this.lastGeocodeConfidence = best.confidence;

      /*   this.messageService.add({
        severity: 'success',
        summary: 'Posizione trovata',
        detail:
          best.confidence === 'exact'
            ? 'Coordinate precise impostate.'
            : 'Coordinate approssimative impostate (centro città/area).',
        life: 3500,
      }); */
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail:
          'Impossibile contattare il servizio di geocoding. Riprova più tardi.',
        life: 5000,
      });
    } finally {
      this.searching = false;
    }
  }

  private syncSelectedFromLatLng(): void {
    if (this.lat === null || this.lng === null) {
      this.selectedPosition = null;
      this.mapCenter = this.defaultCenter;
      this.mapZoom = 5;
      return;
    }

    // Oggetto stabile: non ricreiamo ad ogni change detection
    if (
      !this.selectedPosition ||
      this.selectedPosition.lat !== this.lat ||
      this.selectedPosition.lng !== this.lng
    ) {
      this.selectedPosition = { lat: this.lat, lng: this.lng };
    }
    this.mapCenter = this.selectedPosition;
    // zoom più “vicino” quando c'è una posizione
    this.mapZoom = 16;
  }

  private updateDirty(): void {
    const same = this.lat === this.initialLat && this.lng === this.initialLng;
    this.isDirty = !same;
  }

  async save(): Promise<void> {
    if (!this.referenza) return;
    if (!this.canModify) return;

    this.saving = true;
    try {
      const ref = this.referenza;
      await firstValueFrom(
        this.apiService.updateReferenzaPosizione(ref.id, {
          latitude: this.lat ?? null,
          longitudine: this.lng ?? null,
        }),
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Posizione salvata con successo',
        life: 3000,
      });

      this.posizioneSaved.emit();
      this.close();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile salvare la posizione. Riprova più tardi.',
        life: 5000,
      });
    } finally {
      this.saving = false;
    }
  }
}
